/**
 * voice-v8 — Was die Pegelanzeige zeichnet. Rein, ohne DOM. (03.10.2026, pre.3623)
 *
 * Martin, 03.10.: *„Der Visualizer macht immer wieder Probleme. Er soll sauber
 * angezeigt werden, wenn ich spreche, damit der User wirklich ein schönes
 * Visualisierungselement seiner Sprache sieht und weiß, dass seine Aufnahme
 * aufgenommen wird und alles funktioniert.“*
 *
 * Das Journal 28.09.–03.10. (`[ptt-stille]`, Web-Weg): 104 von 943 Aufnahmen
 * mit Pegel exakt 0/0/0 — am 03.10. elf von vierzehn. Martin: 82, davon 67
 * mit `ptt-wache-null … takt=steht` (dazu `rechentakt-tot wer=pegel`): die
 * Tonverarbeitung der Seite rechnet nicht, die Aufnahme (MediaRecorder) läuft
 * heil weiter. Die übrigen 19 sind ein Konto mit stummer Quelle (Ticket 2643).
 * Die Anzeige zeigte dann die ganze Aufnahme lang ruhende Punkte in voller
 * Deckkraft — genau so sieht sie in einem stillen Raum aus; erst nach ~4,7 s
 * (`rechentakt-tot`) kam ein Satz darunter — und ohne Mitschrift wanderte der
 * Leuchtschein weiter, als hörte das Mikro.
 *
 * Hier steht deshalb, getrennt von der Stille-Entscheidung (mic-visualizer.ts,
 * `merkeStille` — die bleibt unberührt):
 *
 *   - EIN Urteil „kommt überhaupt Signal an?“ (`lage`): ein Bild ohne Signal
 *     ist eines, das nicht gemessen wurde (Kontext angehalten, Takt steht)
 *     ODER exakt digitale Null trägt. Ein echtes Mikrofon liefert in
 *     Gleitkomma nie exakt 0 (Ticket 2643, strom-pegel.ts) — wohl aber eine
 *     stehende Verarbeitung und eine stumme Quelle. Nach BLIND_AB_MS ohne
 *     Signal zeigt die Anzeige „blind“, statt eine leere Fläche als Messung
 *     auszugeben.
 *   - Eine Hüllkurve nach ZEIT, nicht nach Bildern: bisher glättete jedes Bild
 *     mit festem Anteil (0,5 / 0,15). Bei 60 Bildern/s fiel der Pegel in
 *     0,1 s, im nativen 100-ms-Takt in 1,1 s, bei 30 Bildern/s (iPhone unter
 *     Last) dazwischen — dieselbe Stimme sah je Weg anders aus.
 *   - Ein weicher Anschlag statt der harten Kappe: 194 von 837 nicht flachen
 *     Web-Aufnahmen hatten p90 = 1,000, also mindestens jedes zehnte Bild am
 *     Deckel — die Balken standen dort als gerade Wand.
 *   - Der Verlauf wandert in festen Schritten (PROBE_MS) von der Mitte nach
 *     außen; jeder Balken ist ein wirklich gemessener früherer Wert, nichts
 *     erfunden. Bei reduzierter Bewegung wandert nichts.
 */

/** Anstieg der Hüllkurve (Zeitkonstante): ein Wortanfang ist sofort zu sehen. */
const ANSTIEG_MS = 35;
/** Abfall: weich, aber ohne Nachziehen über die nächste Silbe. */
export const ABFALL_MS = 220;
/** Bei reduzierter Bewegung: träger in beide Richtungen. */
const RUHIG_ANSTIEG_MS = 140;
const RUHIG_ABFALL_MS = 450;
/** So oft wandert der Verlauf einen Balken weiter. */
export const PROBE_MS = 60;
/**
 * So lange ohne Signal, bis die Anzeige „blind“ sagt. Kurz genug, dass vor
 * dem ersten Wort klar ist, dass nichts ankommt; lang genug für den Anlauf
 * eines frischen Mikros (die ersten Bilder eines neuen Kontexts sind oft 0)
 * und für den nativen 100-ms-Takt mit verspäteten Antworten.
 */
export const BLIND_AB_MS = 500;
/**
 * Kam so lange kein Bild (Seite verdeckt, Bildschirmtakt stand), beginnt die
 * Frist neu: sonst stünde nach der Rückkehr für ein Bild „blind“ da, bevor
 * der erste Wert ankommt. (pre.3623; wie PEGEL_LUECKE_MS in mic-visualizer.ts)
 */
export const BILD_LUECKE_MS = 1000;
/** Unter dieser Änderung wird nicht neu gezeichnet. */
export const ZEICHNE_AB = 0.01;

/**
 * - misst: die eigene Messung der Seite (oder der native Takt) kommt an.
 * - nachgereicht: die eigene Messung steht, der Pegel kommt aus der Aufnahme am Server — einige Sekunden später
 *   (`neuerNachgereichterPegel`, 08.10.2026).
 * - blind: es kommt gar kein Signal an.
 */
export type PegelLage = "misst" | "nachgereicht" | "blind";

/**
 * Die Kennlinie der Seite: Effektivwert (0…1) → Messwert, ungekappt. Sprache liegt im Effektivwert bei etwa
 * 0,01–0,08; die Wurzel hebt leise Anteile an und hält laute im Rahmen (mic-visualizer.ts, 25.07.2026).
 * Seit 18.10.2026 nur noch für die Stille-Entscheidung (mic-visualizer.ts); die Anzeige rechnet über pegelAusDb.
 */
export function kennlinie(rms: number): number {
  return Math.pow(Math.max(0, rms) * 14, 0.6);
}

/** Darunter gilt ein Fenster als digitale Stille (der Server meldet −120 dBFS für ein Fenster ohne Signal). */
const STUMM_AB_DB = -100;
/** Der Bereich der Anzeige in dBFS: ab UNTEN_DB ruhen die Balken, bei OBEN_DB sind sie voll. */
export const UNTEN_DB = -60;
export const OBEN_DB = -10;
/** Raumrauschen unter diesem Anteil (≈ −57,5 dBFS) bleibt flach — Stille muss nach Stille aussehen. */
const RAUSCH_ANTEIL = 0.05;

/**
 * Ein Pegel in dBFS (Effektivwert) → Ausschlag 0…1: linear in dB zwischen UNTEN_DB und OBEN_DB, das Rauschen
 * darunter bleibt 0. 18.10.2026 (Martin: „Der Equalizer zeigt fast keinen Ausschlag“): die alte Kennlinie
 * `(rms·14)^0,6` mit weichem Anschlag ließ eine leise Stimme (rms 0,003–0,01 bei Mikro ohne Automatik-Pegel)
 * bei 0,16–0,35 stehen; hier liegt sie bei 0,55–0,75. Gleiche Kennlinie für alle drei Quellen.
 */
export function pegelAusDb(db: number): number {
  if (!Number.isFinite(db) || db <= STUMM_AB_DB) return 0;
  const x = Math.max(0, Math.min(1, (db - UNTEN_DB) / (OBEN_DB - UNTEN_DB)));
  return x <= RAUSCH_ANTEIL ? 0 : (x - RAUSCH_ANTEIL) / (1 - RAUSCH_ANTEIL);
}

/** Der Fensterpegel des Servers (server/stille-schwanz.ts `fensterDb`, ganzes Band) → Ausschlag 0…1. */
export function anzeigeWertAusDb(db: number): number {
  // Ein lebendiger, aber leiser Raum (unter der Rauschgrenze) bleibt ein Signal (> 0), nur ohne sichtbaren Ausschlag.
  return Number.isFinite(db) && db > STUMM_AB_DB ? Math.max(pegelAusDb(db), 1e-6) : 0;
}

/** Effektivwert (Quadratmittel, 0…1) → Ausschlag; 0 bei Stille. */
export function anzeigeWertAusQuadratmittel(mittel: number): number {
  return mittel > 0 ? pegelAusDb(10 * Math.log10(mittel)) : 0;
}

/** Der native Pegel (0…1 = (dB+50)/50, PawbotRecorderPlugin.pttLevel) → Ausschlag; 0 = unter −50 dB. */
export function anzeigeWertNativ(level: number): number {
  return Number.isFinite(level) && level > 0 ? pegelAusDb(Math.min(1, level) * 50 - 50) : 0;
}

/** Zeitkonstante, mit der die zuletzt gemessene Spitze nachgibt (Stimme wird leiser → Anzeige zieht langsam nach). */
const SPITZE_ABFALL_MS = 6000;
/** Die Spitze zählt nie kleiner als das: sonst bliese sich ein leises Rauschen auf. */
const SPITZE_MIN = 0.4;
/** Auf diesen Ausschlag wird die kürzliche Spitze gehoben — nie darüber, also nie übersteuert. */
const SPITZE_ZIEL = 0.9;

/**
 * Sanfte Anpassung an die Stimme: hebt Werte so an, dass die Spitze der letzten Sekunden bei SPITZE_ZIEL liegt
 * (höchstens um SPITZE_ZIEL/SPITZE_MIN ≈ 2,25). Reine Anzeige — am Ton und an der Stille-Entscheidung ändert sie nichts.
 * Stille (0) bleibt 0; Rauschen ist schon in pegelAusDb auf 0 gesetzt und wird nicht aufgeblasen.
 */
export function neueAnpassung(): (jetzt: number, wert: number) => number {
  let spitze = SPITZE_MIN;
  let letzte: number | null = null;
  return (jetzt, wert) => {
    // Unsinn (NaN, ±∞) vergiftet die Spitze nicht: kein Wert → 0, keine Zeit → kein Abfall.
    if (!Number.isFinite(wert)) return 0;
    const dt = letzte === null || !Number.isFinite(jetzt) ? 0 : Math.max(0, Math.min(1000, jetzt - letzte));
    if (Number.isFinite(jetzt)) letzte = jetzt;
    wert = Math.max(0, wert);
    spitze = Math.max(SPITZE_MIN, spitze * Math.exp(-dt / SPITZE_ABFALL_MS), wert);
    return Math.min(1, (wert * SPITZE_ZIEL) / spitze);
  };
}

/*
 * ── Der nachgereichte Pegel (08.10.2026) ──────────────────────────────────────────────────────────────────
 *
 * Martin, 08.10. 05:51, mit Bild: „Anzeige hängt – ich höre trotzdem mit“, in der Mitte „Pegel nicht messbar“,
 * die Balken ruhen — *„Das ist auch immer wieder, dass der Pegel nicht angezeigt wird!!“* Die Aufnahme lief, das
 * Nachhören erkannte Text. Ohne Pegel sieht er aber nicht, ob das Mikrofon hört.
 *
 * Das Journal 01.–08.10. (626 Drücke über die Seite): in 53 stand die Tonverarbeitung der Seite (`rechentakt-tot`,
 * Pegel UND Diktat). Jedes Mal hatte kurz vorher der Mikrofon-Motor der App gelauscht; ohne Motor in 0 von 137.
 * Unterbrach der Griff der Sprechtaste die Tonsitzung des Motors, während kein anderer Ton lief (`audio nein`), stand
 * der Takt in 21 von 25 Drücken (als erster toter einer Reihe: 17 von 21; mit `audio ja` als erster: 0 von 93) — und
 * blieb es in den Drücken danach (30 von 44), bis die Sitzung wiederkam. Innerhalb einer Aufnahme kam er nie zurück:
 * alle 53 mit Pegel 0/0/0 und 0 KB Diktat, die Leiter (anhalten/wecken, frischer Kontext) rettete keinen. Die
 * Aufnahme selbst (MediaRecorder) lief immer; das Nachhören lieferte in 52 von 53 schon mit der ersten Probe Text
 * (nach 2,2–5,4 s), in einer erkannte es nie Sprache — der Pegel zeigt dort trotzdem, was ankommt.
 *
 * Darum hier: kommt die eigene Messung nicht an, zeigt die Anzeige den Pegel, den der Server beim Nachhören aus der
 * Aufnahme selbst misst (je 100-ms-Fenster, server/nachhoeren.ts `pegel`). Er ist echt, nur verspätet — um eine
 * Probe (2,5 s) und ihre Laufzeit. Die Anzeige sagt das („verzögert“), statt ihn als Gegenwart auszugeben.
 *
 * ⚠️ NUR die Anzeige. Die Stille-Entscheidung (mic-visualizer.ts `merkeStille`) bekommt davon nichts: sie kennt
 *    beim Nachhören die Stille der Probe (ptt-modal.ts `stilleJetzt`), und ein verspäteter Pegel gäbe ihr eine
 *    Stille, die schon vorbei ist.
 *
 * Abgespielt wird in Aufnahme-Reihenfolge, ein Fenster je NACHGEREICHT_FENSTER_MS — der Verlauf läuft also so
 * schnell wie gesprochen wurde. Vor dem ersten Wert ein kleiner Vorrat (die nächste Probe kommt nicht pünktlich),
 * und höchstens NACHGEREICHT_HOECHSTENS_MS Vorrat, sonst wüchse der Abstand mit jeder langsamen Probe.
 */
/** So lange steht ein nachgereichter Wert — die Fensterlänge am Server (FENSTER_S). */
export const NACHGEREICHT_FENSTER_MS = 100;
/** Bevor ein leerer Vorrat wieder spielt, so viel Zeit: der Takt der Proben schwankt um einige hundert ms. */
export const NACHGEREICHT_VORLAUF_MS = 600;
/** Höchstens so viel ungespielter Vorrat; Älteres fällt weg (der Abstand zur Gegenwart bleibt begrenzt). */
export const NACHGEREICHT_HOECHSTENS_MS = 4000;
/**
 * So lange ohne nachgereichten Wert, bis die Anzeige „blind“ sagt — länger als BLIND_AB_MS. (Prüfbefund K1, 08.10.)
 *
 * Die Proben kommen nicht im Takt: der Ton je Probe wächst in ganzen Sekunden, die Antworten brauchen (Journal
 * 01.–08.10.) im Median 2,52 s, p90 3,05 s, p99 4,77 s. Läuft der Vorrat dazwischen leer, sagte die Anzeige nach
 * 500 ms „Pegel nicht messbar“ — und die Balken sprangen zur Seite (das Schild „verzögert“ steht in der schmalen
 * Linie hinter den Balken, „nicht messbar“ in der Mitte). Nachgerechnet mit den echten Ankunftszeiten: 92-mal in
 * 57 von 70 Aufnahmen; mit 3 s nur noch 2-mal — bei echten Lücken von 9 s, wo „blind“ stimmt. Ein längerer Vorlauf
 * allein (1,5 s) ließ noch 47.
 */
export const NACHGEREICHT_BLIND_AB_MS = 3000;

interface NachgereichterPegel {
  /** Neue Fenster (Ausschlag 0…1, anzeigeWertAusDb) in Aufnahme-Reihenfolge — angekommen um `jetzt`. */
  dazu(jetzt: number, werte: readonly number[]): void;
  /** Der Wert, der jetzt dran ist — `null`, wenn keiner da ist (Vorrat leer oder im Vorlauf). Verbraucht Gespieltes. */
  wert(jetzt: number): number | null;
}

export function neuerNachgereichterPegel(): NachgereichterPegel {
  const vorrat: number[] = [];
  /** Wann vorrat[0] zu spielen beginnt (begann); null = leer. */
  let ab: number | null = null;
  const verbrauche = (jetzt: number): void => {
    if (ab === null) return;
    while (vorrat.length > 0 && jetzt - ab >= NACHGEREICHT_FENSTER_MS) {
      vorrat.shift();
      ab += NACHGEREICHT_FENSTER_MS;
    }
    if (vorrat.length === 0) ab = null;
  };
  return {
    dazu(jetzt, werte) {
      verbrauche(jetzt);
      // Mehr als der Höchstvorrat fiele ohnehin weg — nur die jüngsten ansehen.
      const gute = werte
        .slice(-Math.ceil(NACHGEREICHT_HOECHSTENS_MS / NACHGEREICHT_FENSTER_MS) - 1)
        .filter((w) => Number.isFinite(w))
        .map((w) => Math.max(0, Math.min(1, w)));
      if (gute.length === 0) return;
      ab ??= jetzt + NACHGEREICHT_VORLAUF_MS;
      vorrat.push(...gute);
      // Was über den Höchstvorrat hinaus noch zu spielen wäre, fällt vorne weg — die Anzeige springt nach vorn.
      const gespielt = Math.max(0, jetzt - ab);
      while (vorrat.length > 1 && vorrat.length * NACHGEREICHT_FENSTER_MS - gespielt > NACHGEREICHT_HOECHSTENS_MS) {
        vorrat.shift();
      }
    },
    wert(jetzt) {
      verbrauche(jetzt);
      if (ab === null || jetzt < ab) return null;
      return vorrat[0] ?? null;
    },
  };
}

/** Ein Schritt der Hüllkurve über `dtMs` (zeitbasiert, unabhängig von der Bildrate). */
export function huelleSchritt(vorher: number, ziel: number, dtMs: number, ruhig = false): number {
  if (dtMs <= 0) return vorher;
  const tau = ziel > vorher ? (ruhig ? RUHIG_ANSTIEG_MS : ANSTIEG_MS) : ruhig ? RUHIG_ABFALL_MS : ABFALL_MS;
  return vorher + (ziel - vorher) * (1 - Math.exp(-dtMs / tau));
}

interface PegelBild {
  /** Ausschlag je Balken (0…1). */
  werte: readonly number[];
  lage: PegelLage;
  /** Der Ausschlag jetzt (0…1) — für den Schein. */
  jetzt: number;
}

export interface PegelAnzeige {
  /**
   * Ein Bild der Quelle: Ausschlag (anzeigeWert) oder null = dieses Bild trug kein Signal. `nachgereicht`: der Wert
   * kommt verspätet aus der Aufnahme (neuerNachgereichterPegel) — die Lage sagt es dann.
   */
  messe(jetzt: number, wert: number | null, nachgereicht?: boolean): void;
  /** Rechnet bis `jetzt` und liefert, was zu zeichnen ist. */
  bild(jetzt: number): PegelBild;
}

/**
 * @param balken Anzahl der Balken; die Mitte zeigt den Ausschlag jetzt, nach
 *   außen hin die früheren (gespiegelt).
 * @param ruhig reduzierte Bewegung: kein Wandern, trägere Hülle.
 */
export function neuePegelAnzeige(balken: number, ruhig = false): PegelAnzeige {
  const haelfte = Math.max(1, Math.ceil(balken / 2));
  /** Frühere Ausschläge, [0] = jüngster; die Mitte zeigt die Hülle selbst. */
  const verlauf = new Array<number>(haelfte).fill(0);
  let ziel = 0;
  let huelle = 0;
  let beginn: number | null = null;
  let letztesSignal: number | null = null;
  /** Kam das letzte Signal nachgereicht? */
  let signalNachgereicht = false;
  let letztesBild: number | null = null;
  let naechsteProbe = 0;
  const mitte = (balken - 1) / 2;

  return {
    messe(jetzt, wert, nachgereicht = false) {
      beginn ??= jetzt;
      if (wert === null || !Number.isFinite(wert)) {
        ziel = 0;
        return;
      }
      ziel = Math.max(0, Math.min(1, wert));
      letztesSignal = jetzt;
      signalNachgereicht = nachgereicht;
    },
    bild(jetzt) {
      beginn ??= jetzt;
      if (letztesBild !== null && jetzt - letztesBild > BILD_LUECKE_MS) {
        beginn = jetzt;
        letztesSignal = null;
      }
      // Nachgereichte Werte kommen in Schüben (je Probe) — ihre Lücken sind kein Ausfall (NACHGEREICHT_BLIND_AB_MS).
      const frist = signalNachgereicht ? NACHGEREICHT_BLIND_AB_MS : BLIND_AB_MS;
      const lage: PegelLage =
        jetzt - (letztesSignal ?? beginn) >= frist ? "blind" : signalNachgereicht ? "nachgereicht" : "misst";
      // Ein Bildschirm-Stocken (verdeckt, Last) springt nicht über Sekunden.
      const dt = letztesBild === null ? 0 : Math.min(250, jetzt - letztesBild);
      letztesBild = jetzt;
      huelle = huelleSchritt(huelle, lage === "blind" ? 0 : ziel, dt, ruhig);
      if (huelle < 0.002) huelle = 0;
      if (naechsteProbe === 0 || jetzt - naechsteProbe > 4 * PROBE_MS) naechsteProbe = jetzt + PROBE_MS;
      while (jetzt >= naechsteProbe) {
        verlauf.unshift(huelle);
        verlauf.length = haelfte;
        naechsteProbe += PROBE_MS;
      }
      const werte: number[] = [];
      for (let i = 0; i < balken; i++) {
        const abstand = Math.abs(i - mitte);
        const k = Math.floor(abstand);
        if (ruhig) {
          // Kein Wandern: alle zeigen den Ausschlag jetzt, zur Seite flacher.
          werte.push(huelle * (1 - 0.45 * (abstand / Math.max(1, mitte)) ** 2));
        } else {
          werte.push(k === 0 ? huelle : (verlauf[k - 1] ?? 0));
        }
      }
      return { werte, lage, jetzt: huelle };
    },
  };
}
